import { arrow, autoUpdate, computePosition, flip, offset, shift, size } from "@floating-ui/dom";
import { signal } from "@preact/signals";
/** @import { Signal } from "@preact/signals"; */

/** @typedef {import("@floating-ui/dom").Placement} Placement */

/**
 * @typedef {object} FloatStyles
 * @property {Record<string, string>} float
 * @property {Record<string, string>} arrow
 */

/**
 * @typedef {object} Options
 * @property {Placement} [placement]
 * @property {number} [offset]
 * @property {"auto" | "match"} [width]
 */

export default class Float {
	/** @type {HTMLElement | undefined} */
	#reference;
	/** @type {HTMLElement | undefined} */
	#floating;
	/** @type {HTMLElement | undefined} */
	#arrow;

	/** @type {Required<Options>} */
	#options = { placement: "top", offset: 6, width: "auto" };

	#placement = signal("top");
	get placement() {
		return this.#placement.value;
	}

	get side() {
		return this.placement.split("-")?.[0] ?? this.#options.placement;
	}

	/** @type {Signal<FloatStyles>} */
	#styles = signal({ float: {}, arrow: {} });
	get styles() {
		return this.#styles.value;
	}

	/** @param {Options} options */
	constructor(options = {}) {
		Object.assign(this.#options, options);

		this.#placement.value = this.#options.placement;
	}

	async #update() {
		if (!this.#reference || !this.#floating) return;

		// reset so we can measure the content's full height
		this.#floating.style.maxHeight = "";

		const width = this.#options.width;
		const middleware = [
			offset(this.#options.offset),
			flip(),
			shift({ padding: 6 }),
			size({
				padding: 6,
				apply({ rects, availableHeight, elements }) {
					elements.floating.style.maxHeight = `${Math.max(0, availableHeight)}px`;
					if (width === "match") elements.floating.style.minWidth = `${rects.reference.width}px`;
				},
			}),
		];
		if (this.#arrow) middleware.push(arrow({ element: this.#arrow }));

		const pos = await computePosition(this.#reference, this.#floating, {
			middleware,
			placement: this.#options.placement,
		});

		this.#placement.value = pos.placement;

		/** @type {FloatStyles} */
		const styles = { float: {}, arrow: {} };

		if (this.#floating) {
			styles.float.top = `${pos.y}px`;
			styles.float.left = `${pos.x}px`;
		}

		if (this.#arrow) {
			const arrow = pos.middlewareData.arrow;
			if (arrow?.x) styles.arrow.left = `${arrow.x}px`;
			if (arrow?.y) styles.arrow.top = `${arrow.y}px`;
		}

		this.#styles.value = styles;
	}

	/** @type {(() => void) | undefined} */
	#cleanup;

	/** @param {HTMLElement} el */
	setReferenceRef = el => {
		this.#reference = el;
		return () => {
			this.hide();
			this.#reference = undefined;
		};
	};

	/** @param {HTMLElement} el */
	setFloatingRef = el => {
		this.#floating = el;
		return () => {
			this.hide();
			this.#floating = undefined;
		};
	};

	/** @param {HTMLElement} el */
	setArrowRef = el => {
		this.#arrow = el;
		return () => {
			this.hide();
			this.#arrow = undefined;
		};
	};

	/** @param {HTMLElement} [ref] */
	async show(ref) {
		// if we're already showing, don't run again
		if (this.#cleanup) return;

		if (ref) this.#reference = ref;

		// don't show if we don't have both the reference and floating element
		if (!this.#reference || !this.#floating) return;

		// compute the position
		await this.#update();

		// update the position on changes
		this.#cleanup = autoUpdate(this.#reference, this.#floating, () => this.#update());
	}

	async hide() {
		this.#cleanup?.();
		this.#cleanup = undefined;
	}
}
